/** Who is signed in, and how they got here (adapted from istok-classroom).
 *
 * Three ways in, one session shape: password (POST /auth/v1/login; a self-signed-up teacher is
 * refused with `email_unconfirmed` until the confirmation link is followed), a guest or magic-link
 * grant (POST /auth/v1/magic-link/exchange, or the session join in STORY-016), or a session
 * restored from storage. Teachers persist across tabs (localStorage); guests and students use
 * sessionStorage so closing the tab ends the sitting. A host or paired-phone token (`lt.`,
 * STORY-073) lives in localStorage with no refresh token; tokens are read from whichever
 * storage holds them (tokenStore.ts), so that case is not special here.
 *
 * The access token is an encrypted JWE the client cannot read; identity comes from
 * GET /auth/v1/me, never from parsing the token.
 */
import { serviceUrl } from "../api/base";
import { storageKey } from "../product";
import { parsePolicy, resendOutcome, type ResendOutcome, type SignupPolicy } from "./signupRules";
import { isLocalToken, readToken, writeToken } from "./tokenStore";

export { isLocalToken };

const ACCESS = storageKey("access");
const REFRESH = storageKey("refresh");

export type Role = "guest" | "student" | "teacher" | "developer" | "admin" | "service";

export type Principal = {
  id: string;
  role: Role;
  email: string | null;
  username: string | null;
  email_verified: boolean;
};

export const accessToken = (): string | null => readToken(ACCESS);
export const isSignedIn = (): boolean => accessToken() !== null;

export function keepTokens(access: string, refresh: string | null, persistent = false): void {
  writeToken(ACCESS, access, persistent);
  writeToken(REFRESH, refresh, persistent);
}

export function dropSession(): void {
  writeToken(ACCESS, null, false);
  writeToken(REFRESH, null, false);
}

/** One refresh at a time: concurrent 401s share the in-flight attempt. */
let refreshing: Promise<boolean> | null = null;

export function refreshSession(): Promise<boolean> {
  if (refreshing) return refreshing;
  const token = readToken(REFRESH);
  if (!token) return Promise.resolve(false); // a guest has none by design
  const persistent = (() => {
    try {
      return localStorage.getItem(REFRESH) !== null;
    } catch {
      return false;
    }
  })();
  refreshing = (async () => {
    try {
      const res = await fetch(serviceUrl("/auth/v1/refresh"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: token }),
      });
      if (!res.ok) {
        dropSession();
        return false;
      }
      const pair = (await res.json()) as { access_token: string; refresh_token: string };
      keepTokens(pair.access_token, pair.refresh_token, persistent);
      return true;
    } catch {
      return false; // offline: keep what we have and let the caller surface the failure
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

/** A refused auth call: the issuer's sentence, and its machine code when it sent one
 * (`email_unconfirmed`: a self-signed-up teacher who has not followed the link yet). */
export class AuthRefused extends Error {
  readonly status: number;
  readonly code: string | null;
  constructor(message: string, status: number, code: string | null) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function authPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(serviceUrl(`/auth/v1${path}`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  const parsed: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const field = (k: string): string | null => {
      const v = parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>)[k] : undefined;
      return typeof v === "string" ? v : null;
    };
    throw new AuthRefused(field("detail") ?? `HTTP ${res.status}`, res.status, field("code"));
  }
  return parsed as T;
}

export async function signIn(identifier: string, password: string, remember = true): Promise<Principal> {
  const pair = await authPost<{ access_token: string; refresh_token: string }>("/login", {
    identifier,
    password,
  });
  keepTokens(pair.access_token, pair.refresh_token, remember);
  return me();
}

export async function me(): Promise<Principal> {
  const res = await fetch(serviceUrl("/auth/v1/me"), {
    headers: accessToken() ? { Authorization: `Bearer ${accessToken()}` } : {},
  });
  if (res.status === 401 && (await refreshSession())) return me();
  if (!res.ok) {
    dropSession();
    throw new Error("not signed in");
  }
  return (await res.json()) as Principal;
}

export const isStaff = (role: Role): boolean =>
  role === "teacher" || role === "developer" || role === "admin";

/** Does this server let a teacher open their own account (services/auth signup.py)? */
export async function teacherSignupPolicy(): Promise<SignupPolicy> {
  try {
    const res = await fetch(serviceUrl("/auth/v1/signup/teacher"));
    if (!res.ok) return "off";
    return parsePolicy(await res.json());
  } catch {
    return "off";
  }
}

/** The status of a refused sign-up, for `signupErrorKey`; 0 when the server was not reached. */
export class SignupRefused extends Error {
  readonly status: number;
  constructor(status: number) {
    super(`sign-up refused (${status})`);
    this.status = status;
  }
}

/** Open a teacher account. No tokens come back: the account is usable only once the link in
 * the confirmation mail has been followed, so the page then says to check the inbox. */
export async function signUpTeacher(form: { email: string; password: string; display_name?: string }): Promise<void> {
  let res: Response;
  try {
    res = await fetch(serviceUrl("/auth/v1/signup/teacher"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
  } catch {
    throw new SignupRefused(0);
  }
  if (!res.ok) throw new SignupRefused(res.status);
}

/** Ask for a fresh confirmation link (POST /auth/v1/signup/teacher/resend). The server answers
 * the same for every address, so this only says whether the ask got through. */
export async function resendConfirmation(email: string): Promise<ResendOutcome> {
  try {
    const res = await fetch(serviceUrl("/auth/v1/signup/teacher/resend"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: email.trim() }),
    });
    return resendOutcome(res.status);
  } catch {
    return resendOutcome(0);
  }
}

/** Follow the confirmation link from the sign-up mail (GET /auth/v1/verify). */
export async function confirmEmail(token: string): Promise<boolean> {
  try {
    const res = await fetch(serviceUrl(`/auth/v1/verify?token=${encodeURIComponent(token)}`));
    return res.ok;
  } catch {
    return false;
  }
}
